export const DEFAULT_PATH_TEMPLATES = {
  debitNote: "Debit Note/{year}/{yearMonth}/{code}",
  order: "Orders/{year}/{yearMonth}/{code}",
  renewal: "Renewals/{year}/{yearMonth}/{code}",
  client: "Clients/{code}",
  fileGroup: "File Groups/{code}",
  default: "Uploads/{uploadFolder}/{year}/{month}/{day}/{entityId}",
};

export const DEFAULT_ENTITY_TYPE_ENABLED = {
  debitNote: true,
  order: true,
  renewal: true,
  client: true,
  fileGroup: true,
  default: true,
};

export const ENTITY_BACKUP_TYPE_META = [
  {
    key: "debitNote",
    label: "Debit Note",
    description:
      "Internal staff files attached to GI debit notes (the main files section — not consultant-shared copies).",
  },
  {
    key: "order",
    label: "Order",
    description:
      "Files attached to insurance orders / policies in the order module.",
  },
  {
    key: "renewal",
    label: "Renewal",
    description: "Files attached to renewal records and renewal workflows.",
  },
  {
    key: "client",
    label: "Client",
    description:
      "Files attached to client profiles, such as KYC documents and client-level uploads.",
  },
  {
    key: "fileGroup",
    label: "File Group",
    description:
      "Files uploaded through MBID File Manager folders — shared document libraries, not debit notes or orders.",
  },
  {
    key: "default",
    label: "Other uploads",
    description:
      "Generic uploads without a specific entity type: chat attachments, images, email imports, quotations, payroll, and other modules not listed above.",
  },
];

export const DEFAULT_SYNC_FIELDS = ["files"];

export const DEFAULT_WORKER = {
  maxRetries: 5,
  batchSize: 20,
};

export const normalizePathTemplates = (templates = {}) => ({
  debitNote: templates.debitNote || DEFAULT_PATH_TEMPLATES.debitNote,
  order: templates.order || DEFAULT_PATH_TEMPLATES.order,
  renewal: templates.renewal || DEFAULT_PATH_TEMPLATES.renewal,
  client: templates.client || DEFAULT_PATH_TEMPLATES.client,
  fileGroup: templates.fileGroup || DEFAULT_PATH_TEMPLATES.fileGroup,
  default: templates.default || DEFAULT_PATH_TEMPLATES.default,
});

export const normalizeEntityTypeEnabled = (entityTypeEnabled = {}) => ({
  debitNote: entityTypeEnabled.debitNote !== false,
  order: entityTypeEnabled.order !== false,
  renewal: entityTypeEnabled.renewal !== false,
  client: entityTypeEnabled.client !== false,
  fileGroup: entityTypeEnabled.fileGroup !== false,
  default: entityTypeEnabled.default !== false,
});

export const normalizeWorker = (worker = {}) => ({
  maxRetries:
    Number.isFinite(Number(worker?.maxRetries))
      ? Number(worker.maxRetries)
      : DEFAULT_WORKER.maxRetries,
  batchSize:
    Number.isFinite(Number(worker?.batchSize))
      ? Number(worker.batchSize)
      : DEFAULT_WORKER.batchSize,
});

export const parseServiceAccountEmailFromJson = (raw) => {
  if (!raw || !String(raw).trim()) return "";
  try {
    const parsed = JSON.parse(String(raw).trim());
    return String(parsed?.client_email || "").trim();
  } catch {
    return "";
  }
};

export const normalizeFileBackupSettings = (fileBackup = {}) => ({
  enabled: Boolean(fileBackup?.enabled),
  provider: fileBackup?.provider || "googleDrive",
  googleDrive: {
    rootFolderId: String(fileBackup?.googleDrive?.rootFolderId || "").trim(),
    trashOnDelete: fileBackup?.googleDrive?.trashOnDelete !== false,
  },
  pathTemplates: normalizePathTemplates(fileBackup?.pathTemplates),
  entityTypeEnabled: normalizeEntityTypeEnabled(fileBackup?.entityTypeEnabled),
  syncFields: ["files"],
  worker: normalizeWorker(fileBackup?.worker),
  credentialsConfigured: Boolean(fileBackup?.credentialsConfigured),
  serviceAccountEmail: fileBackup?.serviceAccountEmail || "",
});

export const getFileBackupApiErrorMessage = (result, getErrorMsg, fallback = "") => {
  const presentation = getFileBackupErrorPresentation(result, getErrorMsg);
  if (presentation?.summary) return presentation.summary;
  const detail = getErrorMsg?.(result?.error);
  if (detail && String(detail).trim()) {
    return String(detail).trim();
  }
  return result?.message || fallback;
};

export const getFileBackupErrorPresentation = (result, getErrorMsg) => {
  const structured = result?.error?.fileBackupError;
  if (structured?.title && structured?.summary) {
    return structured;
  }

  const msg = getErrorMsg?.(result?.error);
  if (!msg || !String(msg).trim()) {
    return null;
  }

  const text = String(msg).trim();
  if (/not shared|not accessible|not found|404|notFound/i.test(text)) {
    return {
      title: "Backup folder not shared with MBID",
      summary:
        "MBID signed in to Google, but cannot open the backup folder yet. Share the folder with the service account email (Editor access), then test again.",
      steps: [
        "Open the folder in Google Drive.",
        "Click Share and add the service account email as Editor.",
        "Click Test connection again.",
      ],
      folderId: "",
      serviceAccountEmail: "",
      technical: text,
    };
  }

  return {
    title: "Something went wrong",
    summary: text,
    steps: [],
    folderId: "",
    serviceAccountEmail: "",
    technical: "",
  };
};

export const serializeFileBackupSettings = ({
  formValues,
  credentialsConfigured,
  serviceAccountEmail,
}) => {
  const payload = {
    enabled: Boolean(formValues.enabled),
    provider: formValues.provider || "googleDrive",
    googleDrive: {
      rootFolderId: String(formValues.googleDrive?.rootFolderId || "").trim(),
      trashOnDelete: formValues.googleDrive?.trashOnDelete !== false,
    },
    pathTemplates: normalizePathTemplates(formValues.pathTemplates),
    entityTypeEnabled: normalizeEntityTypeEnabled(formValues.entityTypeEnabled),
    syncFields: ["files"],
    worker: normalizeWorker(formValues.worker),
  };

  const json = String(formValues.serviceAccountJson || "").trim();
  if (json) {
    payload.serviceAccountJson = json;
  }

  return {
    ...payload,
    credentialsConfigured,
    serviceAccountEmail,
  };
};

export const validateFileBackupSettings = (formValues = {}) => {
  if (formValues.enabled) {
    if (!String(formValues.googleDrive?.rootFolderId || "").trim()) {
      return "Root folder ID is required when sync is enabled";
    }
    if (
      !formValues.credentialsConfigured &&
      !String(formValues.serviceAccountJson || "").trim()
    ) {
      return "Service account JSON is required when sync is enabled";
    }
    const enabledTypes = normalizeEntityTypeEnabled(formValues.entityTypeEnabled);
    const hasAnyEnabled = Object.values(enabledTypes).some(Boolean);
    if (!hasAnyEnabled) {
      return "Enable at least one file type on the Path templates tab";
    }
  }
  return null;
};
